import React from 'react';
import { MessageSquare } from 'lucide-react';
import ResultsArea from '@/components/analysis/ResultsArea';
import { FeedbackButton } from './FeedbackButton';
import { DetectionResult } from '@/services/fraudDetection/ml/mlServiceTypes';
import { AnalysisResult } from '@/hooks/analysis/types';
import { ResultLevel } from '@/components/AnalysisResults';
import { saveAs } from 'file-saver';

type AnalysisStatus = 'idle' | 'processing' | 'completed' | 'error';

interface ResultsAreaWithFeedbackProps {
  results: DetectionResult | null;
  isLoading: boolean;
  progress: number;
  analysisStatus: AnalysisStatus;
  partialResults?: Partial<DetectionResult>;
  originalText?: string;
  onNewAnalysis?: () => void;
}

export function ResultsAreaWithFeedback({
  results,
  isLoading,
  progress,
  analysisStatus,
  partialResults,
  originalText,
  onNewAnalysis = () => {}
}: ResultsAreaWithFeedbackProps) {
  // Format results for the ResultsArea component
  const formattedResults: AnalysisResult | null = results ? {
    results: [
      {
        type: "AI Content Detection",
        level: (results.score > 0.6 ? "fraudulent" : results.score > 0.3 ? "suspicious" : "safe") as ResultLevel,
        score: Math.round(results.score * 100),
        details: results.explanation || "No additional details available."
      },
      {
        type: "Linguistic Analysis",
        level: (results.score > 0.5 ? "fraudulent" : results.score > 0.25 ? "suspicious" : "safe") as ResultLevel,
        score: Math.round(results.score * 100),
        details: formatLinguisticDetails(results)
      }
    ],
    overallStatus: (results.score > 0.6 ? "fraudulent" : results.score > 0.3 ? "suspicious" : "safe") as ResultLevel
  } : null;

  // Handle export functionality
  const handleExport = () => {
    if (!results || !formattedResults) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      originalText,
      analysis: {
        overallStatus: formattedResults.overallStatus,
        aiContentDetection: formattedResults.results[0],
        linguisticAnalysis: formattedResults.results[1],
        rawScore: results.score,
        textMetrics: results.textMetrics || {},
        flags: results.flags || []
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });

    saveAs(blob, `analysis-results-${new Date().toISOString()}.json`);
  };

  return (
    <div className="space-y-6">
      <ResultsArea 
        results={formattedResults} 
        isLoading={isLoading || analysisStatus === 'processing'} 
        progress={progress} 
        analysisStatus={analysisStatus} 
        partialResults={partialResults as any}
        icon={MessageSquare}
        onNewAnalysis={onNewAnalysis}
        onExport={handleExport}
      />
      
      {results && originalText && (
        <FeedbackButton 
          originalText={originalText}
          detectionResult={results}
        />
      )}
    </div>
  );
}

function formatLinguisticDetails(results: DetectionResult): string {
  if (!results.textMetrics) {
    return "Linguistic metrics unavailable.";
  }
  
  let details = `Word count: ${results.textMetrics.wordCount || 'N/A'}. `;
  
  if (results.textMetrics.lexicalDiversity) {
    details += `Lexical diversity: ${results.textMetrics.lexicalDiversity}. `;
  }
  
  if (results.textMetrics.readabilityScore !== undefined) {
    details += `Readability: ${Math.round(results.textMetrics.readabilityScore)}. `;
  }
  
  if (results.textMetrics.sentimentBalance !== undefined) {
    const sentiment = results.textMetrics.sentimentBalance;
    const sentimentDesc = 
      sentiment > 0.2 ? "notably positive" :
      sentiment > 0.05 ? "somewhat positive" :
      sentiment < -0.2 ? "notably negative" :
      sentiment < -0.05 ? "somewhat negative" : 
      "neutral";
    
    details += `Sentiment: ${sentimentDesc}. `;
  }
  
  if (results.textMetrics.advancedMetrics) {
    const advMetrics = results.textMetrics.advancedMetrics;
    
    if (advMetrics.formulaicScore !== undefined) {
      details += `Formulaic structure: ${Math.round(advMetrics.formulaicScore * 100)}%. `;
    }
    
    if (advMetrics.sentenceVariability !== undefined) {
      details += `Sentence variability: ${Math.round(advMetrics.sentenceVariability * 100)}%. `;
    }
  }
  
  if (results.flags && results.flags.length > 0) {
    details += `\n\nFlags: ${results.flags.join(". ")}`;
  }
  
  return details;
}
